'use client'

import { AlertIcon } from '@repo/ui/AlertIcon'
import {
  SingleSelectDataGridFilter,
  TextFieldDataGridFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { CustomToolbarTableModule } from '../CustomToolbarTableModule/CustomToolbarTableModule'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRenderHeaderFilterProps,
  GridSortModel,
  GridColumnMenu,
  GridColumnMenuProps,
  gridColumnDefinitionsSelector,
  GridPinnedColumnFields,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { LinkWithClientMatterNo } from '@repo/app-assets/LinkWithClientMatterNo'
import { MoreMenuCard } from '@repo/clients-matters-tables/MoreMenuCard'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useClientTableStore } from '@repo/store/useClientTableStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import defaultClientsTableConfig from '@repo/store/defaultClientsTableConfig'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const ClientsTableModule = () => {
  // Using our store instead of context.
  const {
    clientsTableFilters,
    setClientsTableFilters,
    setClientsTableFiltersFromPreferences,
    resetClientTableFilters,
    clientsTableFilterCount
  } = useDataGridFilterStore()
  const { Clients: clientsPreferences, updateUserPreference } = useUserPreferencesStore()

  const { currentClientMatterNo } = useMattersStore()
  const { fetchClientsTableData, clientsTableData } = useClientTableStore()
  const apiRef = useGridApiRef()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { height: windowHeight } = useWindowSize()
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)

  const tableConfig = clientsPreferences?.CLIENTS_TABLE_CONFIG?.resolvedValue || defaultClientsTableConfig
  if (!tableConfig) console.error('No table config found')

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmpty = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmpty(v)) return false

    if (Array.isArray(v)) {
      return v.some((item) => !isEmpty(item))
    }

    switch (typeof v) {
      case 'boolean':
        // treat both true and false as "set"
        return true
      case 'number':
        return true // 0 can be meaningful
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmpty((v as any).value)
        if ('date' in v) return !isEmpty((v as any).date)
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const hasActiveFilters = useMemo(() => {
    const f = clientsTableFilters?.filters
    if (!f) return false
    return Object.values(f).some((v) => isFilterValueActive(v))
  }, [clientsTableFilters?.filters])

  const hydratedFromPrefsRef = useRef(false)
  useEffect(() => {
    if (!tableConfig || hydratedFromPrefsRef.current) return
    if (!hasActiveFilters) {
      setClientsTableFiltersFromPreferences({ filters: tableConfig?.filters || null })
    }
    hydratedFromPrefsRef.current = true
  }, [tableConfig, hasActiveFilters, setClientsTableFiltersFromPreferences])

  const [loading, setLoading] = useState(false)
  const [columnOrder, setColumnOrder] = useState(tableConfig?.columnOrder || [])
  const [columnWidths, setColumnWidths] = useState(tableConfig?.columnWidths || {})
  const [sortModel, setSortModel] = useState<GridSortModel>(tableConfig?.columnSort || [])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    tableConfig?.columnVisibilityModel || {}
  )
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({
    left: tableConfig?.columnsPinnedLeft || [],
    right: tableConfig?.columnsPinnedRight || []
  })

  const updateTableConfigPreference = (fieldsToChange: any) => {
    const tableConfigPreferenceId = clientsPreferences?.CLIENTS_TABLE_CONFIG?.userConfigurationTypeId || null
    if (!tableConfigPreferenceId) return

    updateUserPreference({
      userConfigurationTypeId: tableConfigPreferenceId,
      value: {
        ...tableConfig,
        columnOrder: columnOrder,
        columnVisibilityModel: columnVisibilityModel,
        columnWidths: columnWidths,
        columnsPinnedLeft: pinnedColumns.left,
        columnsPinnedRight: pinnedColumns.right,
        columnSort: sortModel,
        ...fieldsToChange
      },
      convertJsonToString: true,
      categoryName: 'Clients'
    })
  }

  const onPinnedColumnsChange = useCallback((updatedPinnedColumns: GridPinnedColumnFields) => {
    setPinnedColumns(updatedPinnedColumns)
    updateTableConfigPreference({
      columnsPinnedLeft: updatedPinnedColumns.left,
      columnsPinnedRight: updatedPinnedColumns.right
    })
  }, [])

  const onSortModelChange = (model: any) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: any) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const columnOrder = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(columnOrder)
    updateTableConfigPreference({ columnOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const columnWidths: any = {}
    columnDefinitions.forEach((column: any) => {
      columnWidths[column.field] = column.width
    })
    setColumnWidths(columnWidths)
    updateTableConfigPreference({ columnWidths })
  }

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)

    // Stringify the value (for small objects this is OK)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)

    if (signal !== prevSignal) {
      ref.current = value
    }

    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(clientsTableFilters)

  // Data fetching effect
  useEffect(() => {
    const getTableData = async () => {
      setLoading(true)

      const computedOrderBy = sortModel
        .map((sortItem) => {
          // Skip "moreMenu" if it's not meant to be sorted.
          if (sortItem.field === 'moreMenu') return null
          // Map clientMatterNo to clientNo.
          if (sortItem.field === 'clientMatterNo') {
            return { columnName: 'clientNo', direction: sortItem.sort === 'asc' ? 0 : 1 }
          }
          return { columnName: sortItem.field, direction: sortItem.sort === 'asc' ? 0 : 1 }
        })
        .filter(Boolean)

      // Check if computedOrderBy has an object { columnName: null, direction: 0 }.
      const orderByColumns = computedOrderBy.some((item: any) => item.columnName === null && item.direction === 0)
        ? null
        : computedOrderBy

      setClientsTableFilters({
        clientMatterNo: 'clientMatterNo',
        value: currentClientMatterNo
      })

      setClientsTableFilters({
        orderByColumns: 'orderByColumns',
        value: orderByColumns
      })

      setClientsTableFilters({
        startAtIndex: 'startAtIndex',
        value: deepFilters.startAtIndex ?? 0
      })

      setClientsTableFilters({
        pageSize: 'pageSize',
        value: deepFilters.pageSize ?? 25
      })

      const deepFiltersFiltersString = JSON.stringify(deepFilters.filters)
      const currentTableConfigFiltersString = JSON.stringify(tableConfig.filters)
      const filtersHaveChanged = deepFiltersFiltersString !== currentTableConfigFiltersString

      if (filtersHaveChanged) {
        updateTableConfigPreference({
          filters: deepFilters.filters
        })
      }

      await fetchClientsTableData()

      setLoading(false)
    }

    getTableData()
  }, [sortModel, deepFilters.pageSize, deepFilters.filters, deepFilters.startAtIndex])

  useEffect(() => {
    setClientsTableFilters({ totalRowCount: 'totalRowCount', value: clientsTableData.totalRowCount })
  }, [clientsTableData.totalRowCount, setClientsTableFilters])

  const renderAddress = (row: any) => {
    const { house, area, postalTown, county, postcode } = row
    const addressArray = [house, area, postalTown, county, postcode].filter(Boolean)

    if (!addressArray?.length) return ''

    return addressArray.join(', ')
  }

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 216 : 146
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  const columns: any = [
    {
      field: 'clientNo',
      // @ts-ignore
      id: 'clientNo',
      headerName: 'Client no',
      width: 160,
      type: 'string',
      renderCell: (params: any) => <ClientMatterDetailsPill clientNo={params.row.clientNo} sx={{ top: '3px' }} />,
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="clientNo" isClientsTable={true} {...params} />
      )
    },
    {
      field: 'clientNames',
      headerName: 'Client names',
      width: 260,
      sortable: false,
      type: 'string',
      renderCell: (params: any) => {
        const clientNames = params.row.clientNames
        if (clientNames.length > 1) {
          return (
            <MUITooltip
              placement="bottom"
              arrow
              sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
              title={clientNames.map((client: any) => (
                <MUITypography
                  key={client.name}
                  variant="body2"
                  color="white"
                  sx={{
                    mb: 1,
                    p: 1,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textTransform: 'capitalize',
                    fontSize: '0.75rem',
                    whiteSpace: 'wrap',
                    lineHeight: '1.2rem'
                  }}
                >
                  {client.name}
                </MUITypography>
              ))}
            >
              <LinkWithClientMatterNo
                to={`/case-management/clients/client-workspace`}
                clientMatterNo={`${params.row.clientNo}-${params.row.mostRecentMatterNo || params.row.matterNo}`}
              >
                <MUIStack display="flex" direction="row" gap={1.5} sx={{ cursor: 'pointer' }}>
                  <MUITypography
                    variant="body2"
                    sx={{
                      mb: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textTransform: 'capitalize',
                      fontSize: '0.875rem',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    {clientNames[0].name}
                  </MUITypography>
                  <MUITypography
                    variant="body2"
                    sx={{
                      mb: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textTransform: 'capitalize',
                      fontSize: '0.875rem',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    +{clientNames.length - 1}
                  </MUITypography>
                </MUIStack>
              </LinkWithClientMatterNo>
            </MUITooltip>
          )
        } else {
          return (
            <MUIStack display="flex" direction="row" gap={1.5}>
              {clientNames.map((client: any) => (
                <LinkWithClientMatterNo
                  to={`/case-management/clients/client-workspace`}
                  clientMatterNo={`${params.row.clientNo}-${params.row.mostRecentMatterNo || params.row.matterNo}`}
                  key={params.row.clientNo}
                >
                  <MUITypography
                    key={client.name}
                    variant="body2"
                    sx={{
                      mb: 0,
                      fontSize: '0.875rem',
                      cursor: client.name !== '' ? 'pointer' : 'auto',
                      textDecoration: client.name !== '' ? 'underline' : 'none',
                      textTransform: client.name !== '' ? 'capitalize' : 'none',
                      whiteSpace: 'wrap',
                      lineHeight: '1.2rem'
                    }}
                  >
                    {client.name !== '' ? client.name : '-'}
                  </MUITypography>
                </LinkWithClientMatterNo>
              ))}
            </MUIStack>
          )
        }
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="clientName" isClientsTable={true} {...params} />
      )
    },
    {
      field: 'clientType',
      headerName: 'Client type',
      width: 160,
      type: 'singleSelect',
      renderCell: (params: any) =>
        params.value === 'C' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Company
          </MUITypography>
        ) : params.value === 'I' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Individual
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Not set
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          filterType="clientType"
          label="Select"
          options={['C', 'I']}
          valueMapping={{ C: 'Company', I: 'Individual' }}
          isClientsTable={true}
          {...params}
        />
      )
    },
    {
      field: 'openMattersCount',
      headerName: 'Open matters',
      width: 150,
      type: 'number',
      filterable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {params.value}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'highRisk',
      headerName: 'High risk',
      width: 180,
      type: 'singleSelect',
      renderCell: (params: any) =>
        params.value === true ? (
          <MUITypography
            sx={{
              alignItems: 'center',
              backgroundColor: '#C33241',
              borderRadius: '1rem',
              color: '#fff',
              display: 'flex',
              fontSize: '0.75rem',
              fontWeight: '600',
              height: '25px',
              justifyContent: 'center',
              mb: 0,
              textAlign: 'center',
              width: '94px',
              minWidth: '94px'
            }}
            variant="body2"
          >
            <AlertIcon
              sx={{ height: '1rem', mr: '0.25rem', width: '1rem', [`& > path`]: { fill: '#fff', strokeWidth: '2px' } }}
            />{' '}
            High Risk
          </MUITypography>
        ) : (
          '-'
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          filterType="highRisk"
          label="Select"
          options={[true, false]}
          valueMapping={{ true: 'High risk', false: 'Not high risk' }}
          isClientsTable={true}
          {...params}
        />
      )
    },
    {
      field: 'emailAddress',
      headerName: 'Email',
      flex: 300,
      minWidth: 300,
      type: 'string',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5}>
          <MUITypography variant="body2" sx={{ fontSize: '0.875rem', mb: 0 }} noWrap>
            {params.value !== '' ? params.value : '-'}
          </MUITypography>
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="emailAddress" isClientsTable={true} {...params} />
      )
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 320,
      minWidth: 320,
      type: 'string',
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={renderAddress(params.row)}
        >
          <MUITypography variant="body2" sx={{ fontSize: '0.875rem', mb: 0 }} noWrap>
            {renderAddress(params.row) || '-'}
          </MUITypography>
        </MUITooltip>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="address" isClientsTable={true} {...params} />
      )
    },
    {
      field: 'moreMenu',
      headerName: '',
      width: 49,
      hideable: false,
      filterable: false,
      sortable: false,
      type: 'actions',
      renderCell: (params: any) => (
        <MoreMenuCard
          isClientsTable={true}
          clientNo={params.row.clientNo}
          matterNo={params.row.mostRecentMatterNo}
          rowData={params.row}
        />
      )
    }
  ].sort((a, b) => {
    // sort the columns as per the order in the tableConfig
    const targetIndexA = columnOrder?.indexOf(a.field) || 0
    const targetIndexB = columnOrder?.indexOf(b.field) || 1

    if (targetIndexA > targetIndexB) return 1
    return -1
  })

  const getTogglableColumns = (cols: GridColDef[]) =>
    cols.filter((column) => column.hideable !== false && column.field !== '__check__').map((column) => column.field)

  const paginationModel: GridPaginationModel = {
    page: Math.floor((clientsTableFilters.startAtIndex ?? 0) / (clientsTableFilters.pageSize ?? 25)),
    pageSize: clientsTableFilters.pageSize ?? 25
  }

  return (
    <MUIPaper
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        mt: '9rem',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        [`@media (min-height: 991px)`]: { mt: '-3.5rem' },
        [`& .MuiDataGrid-root`]: {
          border: 'none',
          [`& div[field="clientNames"]`]: { cursor: 'pointer', [`& .MuiStack-root`]: { cursor: 'pointer' } }
        }
      }}
    >
      <TableClearFilters
        filterCount={clientsTableFilterCount}
        onClear={resetClientTableFilters}
        sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
      />
      <DataGridPro
        apiRef={apiRef}
        columns={columns}
        onColumnOrderChange={onColumnOrderChange}
        onColumnWidthChange={onColumnWidthChange}
        columnVisibilityModel={columnVisibilityModel}
        disableVirtualization
        getRowId={(row) => `${row.clientNo}-${uuidv4()}`}
        headerFilters
        pinnedColumns={pinnedColumns}
        loading={loading}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPaginationModelChange={(newModel) => {
          if (newModel.page !== paginationModel.page || newModel.pageSize !== paginationModel.pageSize) {
            setClientsTableFilters({
              pageSize: 'pageSize',
              value: newModel.pageSize
            })
            setClientsTableFilters({
              startAtIndex: 'startAtIndex',
              value: newModel.page * newModel.pageSize
            })
            setClientsTableFilters({
              totalRowCount: 'totalRowCount',
              value: clientsTableData.totalRowCount
            })
          }
        }}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[5, 10, 25, 50]}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={clientsTableData?.totalRowCount || 0}
        rows={clientsTableData.rows ?? []}
        slotProps={{
          columnsManagement: { getTogglableColumns },
          loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
        }}
        sortModel={sortModel}
        sx={{
          [`.MuiDataGrid-root`]: { border: 'none' },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
          [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
          [`& .MuiDataGrid-cell`]: { alignContent: 'center', [`& .MuiTypography-root`]: { top: 'unset' } },
          [`& .MuiTablePagination-selectLabel`]: { display: 'block !important' },
          [`& .MuiTablePagination-input`]: {
            display: 'inline-flex !important',
            mb: 0,
            ml: 0,
            mt: '8px',
            fontSize: '0.875rem',
            [`& .MuiTablePagination-select`]: {
              padding: '0 !important',
              ml: '4px',
              mr: '20px',
              minWidth: 'none',
              [`&:focus`]: { backgroundColor: 'transparent' }
            },
            [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
          }
        }}
        slots={{
          headerFilterMenu: null,
          toolbar: () => <CustomToolbarTableModule />,
          columnMenu: (props: GridColumnMenuProps) => (
            <GridColumnMenu
              {...props}
              // @ts-ignore
              sx={{ '.MuiTypography-body1': { mb: 0 } }}
              slots={{ columnMenuFilterItem: null }}
            />
          )
        }}
      />
    </MUIPaper>
  )
}
